import { useEffect } from "react";
import { createFileRoute, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useSession } from "@/lib/session";
import { Frame } from "@/ui/frame/Frame";
import { Spinner } from "@/ui/Button";
import { ErrorState } from "@/ui/States";
import { WaitingForAccess } from "@/features/access/WaitingForAccess";
import { isSafeRedirect } from "@/lib/redirect";

export const Route = createFileRoute("/_app")({
  component: AuthenticatedLayout,
});

/**
 * The guard for everything behind sign-in.
 *
 * The check is a query, not a stored flag: if the session was revoked
 * elsewhere, the first request after that discovers it and this redirects.
 */
function AuthenticatedLayout() {
  const { user, isLoading, needsSecondFactor, error } = useSession();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  useEffect(() => {
    if (isLoading) return;
    if (needsSecondFactor) {
      void navigate({
        to: "/two-factor",
        search: isSafeRedirect(pathname) ? { redirect: pathname } : {},
        replace: true,
      });
      return;
    }
    if (!user && !error) {
      // The pathname is only offered as a way back when it is somewhere worth
      // going back to. This effect re-runs on every pathname change, so once
      // the navigation below has committed it can fire again with pathname
      // already "/sign-in" — and writing that as the redirect is what sent
      // people back to the login form after a correct second-factor code. See
      // lib/redirect.ts.
      void navigate({
        to: "/sign-in",
        search: isSafeRedirect(pathname) ? { redirect: pathname } : {},
        replace: true,
      });
    }
  }, [isLoading, user, needsSecondFactor, error, navigate, pathname]);

  if (isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <span className="text-[var(--primary)]">
          <Spinner />
        </span>
        <span className="sr-only">Loading your account…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto flex min-h-dvh max-w-lg items-center px-6">
        <ErrorState error={error} onRetry={() => window.location.reload()} />
      </div>
    );
  }

  if (!user) return null;

  // Registering is not being let in. Staff are exempt, or the account that does
  // the approving would be queued behind its own decision.
  if (user.access !== "approved" && !user.is_platform_admin) {
    return <WaitingForAccess user={user} />;
  }

  return (
    <Frame>
      <Outlet />
    </Frame>
  );
}
